import * as XLSX from "xlsx";
import { ValidationResult, ProductMaterialRow, ValidationError } from "./types";
import apiMethods from "../../../../utils/api";

const validateFloat = (value: string): number => {
    if (!value) return 0.0;
    const cleaned = value.replace(/[^\d.]/g, "");
    return cleaned ? parseFloat(cleaned) : 0.0;
};

// Valid product categories from choices_config.json
const VALID_PRODUCT_CATEGORIES = ["Phuy", "Thùng", "Cơ Khí Ô Tô"];

const normalizeProductCategory = (value: string): string => {
    if (!value) return "";
    const trimmed = value.trim();
    
    // Case-insensitive matching
    for (const validCategory of VALID_PRODUCT_CATEGORIES) {
        if (trimmed.toLowerCase() === validCategory.toLowerCase()) {
            return validCategory; // Return exact case from config
        }
    }
    
    // If no match, return as-is (validation will catch it)
    return trimmed;
};

const normalizeMaterialCategory = (value: string): string => {
    if (!value) return "Vật tư chính";
    const normalized = value.trim().toUpperCase();
    
    const categoryMapping: { [key: string]: string } = {
        "NLVC": "Vật tư chính",
        "NVLC": "Vật tư chính",
        "NLVP": "Vật tư phụ",
        "NVLP": "Vật tư phụ",
    };
    
    if (normalized in categoryMapping) {
        return categoryMapping[normalized];
    }
    
    if (normalized === "VẬT TƯ CHÍNH" || normalized === "VẬT TƯ PHỤ") {
        return normalized === "VẬT TƯ CHÍNH" ? "Vật tư chính" : "Vật tư phụ";
    }
    
    return "Vật tư chính";
};

const normalizeStorage = (value: string): string => {
    if (!value) return "Kho 1";
    const parts = value.split('\n').map(p => p.trim()).filter(p => p);
    if (parts.length > 0) {
        const firstPart = parts[0];
        if (firstPart === "Kho 1" || firstPart === "Kho 2") {
            return firstPart;
        }
    }
    return "Kho 1";
};

const findCommonPrefix = (strings: string[]): string => {
    if (strings.length === 0) return "";
    if (strings.length === 1) return strings[0];
    
    let prefix = strings[0];
    for (let i = 1; i < strings.length; i++) {
        const str = strings[i];
        let j = 0;
        while (j < prefix.length && j < str.length && prefix[j] === str[j]) {
            j++;
        }
        prefix = prefix.substring(0, j);
        if (prefix === "") break;
    }
    return prefix;
};

export const validateExcelFile = async (file: File): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                
                let sheetName: string;
                if (workbook.SheetNames && workbook.SheetNames.length > 0) {
                    sheetName = workbook.SheetNames[0];
                } else {
                    const sheetKeys = Object.keys(workbook.Sheets);
                    sheetName = sheetKeys[0] || "";
                }
                
                if (!sheetName || !workbook.Sheets[sheetName]) {
                    reject(new Error(`Không tìm thấy sheet đầu tiên. Các sheet có sẵn: ${workbook.SheetNames.join(", ")}`));
                    return;
                }
                
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

                if (jsonData.length < 3) {
                    reject(new Error("File phải có ít nhất 3 dòng (headers ở dòng 3)"));
                    return;
                }

                // Get headers from row 3 (index 2) - database field names
                const headerRow = jsonData[2];
                if (!headerRow || headerRow.length === 0) {
                    reject(new Error("Không tìm thấy headers ở dòng 3"));
                    return;
                }

                // Map headers to field names (handle duplicate column names)
                const fieldMap: { [key: string]: number } = {};
                let nameCount = 0;
                let categoryCount = 0;

                // Process all cells in header row to preserve column index alignment
                headerRow.forEach((header: any, colIdx: number) => {
                    // Convert to string and trim, but process even if empty to preserve column indices
                    const headerStr = header ? String(header).trim() : "";
                    
                    if (headerStr) {
                        // Handle duplicate column names by counting occurrences
                        if (headerStr === 'name') {
                            if (nameCount === 0) {
                                // First occurrence = product_name (column 2)
                                fieldMap['product_name'] = colIdx;
                            } else if (nameCount === 1) {
                                // Second occurrence = material_name (column 7)
                                fieldMap['material_name'] = colIdx;
                            }
                            nameCount++;
                        } else if (headerStr === 'category') {
                            if (categoryCount === 0) {
                                // First occurrence = product_category (column 3)
                                fieldMap['product_category'] = colIdx;
                            } else if (categoryCount === 1) {
                                // Second occurrence = material_category (column 8)
                                fieldMap['material_category'] = colIdx;
                            }
                            categoryCount++;
                        } else {
                            // For non-duplicate columns, use as-is
                            fieldMap[headerStr] = colIdx;
                        }
                    }
                });

                const rows: ProductMaterialRow[] = [];
                const errors: ValidationError[] = [];
                let originalRowCount = 0;

                // Process data starting from row 4 (index 3)
                for (let rowIdx = 3; rowIdx < jsonData.length; rowIdx++) {
                    const row = jsonData[rowIdx];
                    const rowNumber = rowIdx + 1; // Excel row number (1-based)

                    // Skip empty rows
                    if (!row || row.every((cell: any) => cell === null || cell === undefined || String(cell).trim() === "")) {
                        continue;
                    }
                    
                    originalRowCount++;

                    const rowErrors: ValidationError[] = [];
                    const bomData: any = {};

                    // Process fields
                    Object.entries(fieldMap).forEach(([field, colIdx]) => {
                        try {
                            let value = row[colIdx];
                            
                            // Skip if cell doesn't exist
                            if (value === null || value === undefined) {
                                return;
                            }

                            if (typeof value !== "string") {
                                value = String(value);
                            }
                            
                            // Treat "-" and empty strings as empty/null value
                            const trimmed = value.trim();
                            if (trimmed === "" || trimmed === "-") {
                                return;
                            }
                            
                            value = trimmed;

                            // Apply field-specific validation and normalization
                            if (field === "product_category") {
                                bomData[field] = normalizeProductCategory(value);
                            } else if (field === "material_category") {
                                bomData[field] = normalizeMaterialCategory(value);
                            } else if (field === "storage") {
                                bomData[field] = normalizeStorage(value);
                            } else if (field === "quantity" || field === "measurement" || field === "total_measurement") {
                                bomData[field] = validateFloat(value);
                            } else if (field === "product_code" || field === "product_name" || field === "material_code" || 
                                field === "material_name" || field === "product_category_group" || field === "category_note" || 
                                field === "specification" || field === "note") {
                                value = value.replace(/\s+/g, " ").trim();
                                // Apply length limits
                                if (field === "product_name" || field === "material_name") {
                                    value = value.substring(0, 255);
                                } else if (field === "specification") {
                                    value = value.substring(0, 255);
                                }
                                bomData[field] = value;
                            } else {
                                bomData[field] = value;
                            }
                        } catch (e: any) {
                            rowErrors.push({
                                row: rowNumber,
                                field,
                                error: `Lỗi xử lý trường: ${e.message}`,
                                data: row[colIdx],
                            });
                        }
                    });

                    // Validate required fields
                    if (!bomData.product_name || String(bomData.product_name).trim() === "") {
                        rowErrors.push({
                            row: rowNumber,
                            field: "product_name",
                            error: "Tên sản phẩm là bắt buộc (cột 2)",
                        });
                    }

                    if (!bomData.material_code || String(bomData.material_code).trim() === "") {
                        rowErrors.push({
                            row: rowNumber,
                            field: "material_code",
                            error: "Mã vật tư là bắt buộc (cột 6)",
                        });
                    }

                    // Validate product category if provided
                    if (bomData.product_category && !VALID_PRODUCT_CATEGORIES.includes(bomData.product_category)) {
                        rowErrors.push({
                            row: rowNumber,
                            field: "product_category",
                            error: `Danh mục sản phẩm không hợp lệ: ${bomData.product_category}. Phải là một trong: ${VALID_PRODUCT_CATEGORIES.join(", ")}`,
                        });
                    }

                    // Validate storage if provided
                    if (bomData.storage && bomData.storage !== "Kho 1" && bomData.storage !== "Kho 2") {
                        rowErrors.push({
                            row: rowNumber,
                            field: "storage",
                            error: `Giá trị kho không hợp lệ: ${bomData.storage}. Phải là "Kho 1" hoặc "Kho 2"`,
                        });
                    }

                    rows.push({
                        rowNumber,
                        data: bomData,
                        isValid: rowErrors.length === 0,
                        errors: rowErrors,
                    });

                    if (rowErrors.length > 0) {
                        errors.push(...rowErrors);
                    }
                }

                // Check for duplicate product names with different codes
                const productsByName = new Map<string, { codes: Set<string>; rows: number[] }>();
                rows.forEach((row) => {
                    const productName = row.data.product_name?.trim();
                    const productCode = row.data.product_code?.trim() || "";
                    
                    if (productName) {
                        if (!productsByName.has(productName)) {
                            productsByName.set(productName, { codes: new Set(), rows: [] });
                        }
                        const productInfo = productsByName.get(productName)!;
                        if (productCode) {
                            productInfo.codes.add(productCode);
                        }
                        productInfo.rows.push(row.rowNumber);
                    }
                });

                // Add warnings for duplicate names with different codes
                productsByName.forEach((productInfo, productName) => {
                    if (productInfo.codes.size > 1) {
                        // Multiple different codes for same name
                        const codesArray = Array.from(productInfo.codes);
                        const commonPrefix = findCommonPrefix(codesArray);
                        const suffixes = codesArray.map(code => code.substring(commonPrefix.length)).filter(s => s);
                        
                        productInfo.rows.forEach((rowNum) => {
                            const row = rows.find(r => r.rowNumber === rowNum);
                            if (row) {
                                const rowCode = row.data.product_code?.trim() || "";
                                const suffix = rowCode.substring(commonPrefix.length);
                                
                                if (suffix) {
                                    const warning: ValidationError = {
                                        row: rowNum,
                                        field: "product_name",
                                        error: `Cảnh báo: Tên sản phẩm "${productName}" trùng với sản phẩm khác nhưng có mã khác. Tên sẽ được tự động thêm hậu tố "(${suffix})" để phân biệt.`,
                                        isWarning: true, // Mark as warning, not blocking error
                                    };
                                    row.errors.push(warning);
                                    errors.push(warning);
                                    // Don't mark row as invalid, just add warning
                                }
                            }
                        });
                    }
                });

                // Check material existence after parsing
                const allMaterialCodes = new Set<string>();
                rows.forEach((row) => {
                    const materialCode = row.data.material_code?.trim();
                    if (materialCode) {
                        allMaterialCodes.add(materialCode);
                    }
                });

                // Validate materials exist in database
                if (allMaterialCodes.size > 0) {
                    try {
                        const materialCodesArray = Array.from(allMaterialCodes);
                        // Fetch all materials with a large page size to get all materials
                        const response: any = await apiMethods.get(
                            `/materials/?page_size=${Number.MAX_SAFE_INTEGER}`
                        );
                        const existingMaterials = response?.results || [];
                        const existingMaterialCodes = new Set(
                            existingMaterials.map((m: any) => m.material_code?.trim()).filter((code: string) => code)
                        );

                        // Find missing materials
                        const missingMaterialCodes = materialCodesArray.filter(
                            (code) => !existingMaterialCodes.has(code)
                        );

                        // Add validation errors for missing materials
                        if (missingMaterialCodes.length > 0) {
                            rows.forEach((row) => {
                                const materialCode = row.data.material_code?.trim();
                                if (materialCode && missingMaterialCodes.includes(materialCode)) {
                                    const materialError: ValidationError = {
                                        row: row.rowNumber,
                                        field: "material_code",
                                        error: `Vật tư với mã '${materialCode}' chưa tồn tại trong hệ thống. Vui lòng tạo vật tư trước khi nhập định mức vật tư.`,
                                    };
                                    row.errors.push(materialError);
                                    row.isValid = false;
                                    errors.push(materialError);
                                }
                            });
                        }
                    } catch (apiError: any) {
                        // If API call fails, add a warning but don't block validation
                        const apiErrorMsg: ValidationError = {
                            row: 0,
                            field: "material_code",
                            error: `Không thể kiểm tra sự tồn tại của vật tư: ${apiError.message || "Lỗi không xác định"}`,
                            isWarning: true,
                        };
                        errors.push(apiErrorMsg);
                    }
                }

                resolve({
                    rows,
                    errors,
                    originalRowCount,
                });
            } catch (e: any) {
                reject(new Error(`Lỗi đọc file Excel: ${e.message}`));
            }
        };
        reader.onerror = () => {
            reject(new Error("Lỗi đọc file"));
        };
        reader.readAsArrayBuffer(file);
    });
};

